/**
 * Protected Route Component
 * Wraps routes that require authentication and/or specific roles
 */

import React, { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../contexts/I18nContext';
import type { Role } from 'blia-events-shared';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: Role;
  requireBound?: boolean;
  allowedRoles?: Role[];
}

export function ProtectedRoute({
  children,
  requiredRole,
  requireBound = false,
  allowedRoles,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated, isBound, hasRoleAtLeast } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <div className="loading-spinner">{t('common.loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireBound && !isBound) {
    // Redirect to binding wizard if not bound
    return <Navigate to="/binding" state={{ from: location }} replace />;
  }

  if (requiredRole && !hasRoleAtLeast(requiredRole)) {
    // Redirect to access denied page
    return <Navigate to="/access-denied" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to access denied page
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

// Higher-order component version for convenience
export function withProtectedRoute<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: Omit<ProtectedRouteProps, 'children'> = {}
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithProtection = (props: P) => (
    <ProtectedRoute {...options}>
      <WrappedComponent {...props} />
    </ProtectedRoute>
  );

  ComponentWithProtection.displayName = `ProtectedRoute(${displayName})`;

  return ComponentWithProtection;
}

// Shorthand components for common role requirements
export function SysAdminRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requiredRole="SysAdmin">
      {children}
    </ProtectedRoute>
  );
}

export function OrgAdminRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requiredRole="OrgAdmin">
      {children}
    </ProtectedRoute>
  );
}

export function OrgOrEventAdminRoute({ children }: { children: ReactNode }) {
  const { user, isLoading, isAuthenticated, isBound, hasRoleAtLeast } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isBound) {
    return <Navigate to="/binding" state={{ from: location }} replace />;
  }

  if (!hasRoleAtLeast('OrgAdmin') && !user?.isEventAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

export function MemberRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requireBound>
      {children}
    </ProtectedRoute>
  );
}

export default ProtectedRoute;
